<template>
  <div v-if="user && user._id" class="customer-info-editor">
    <section class="info-panel">
      <header class="info-panel__header">
        <span class="info-panel__icon info-panel__icon--green"><v-icon>mdi-account-outline</v-icon></span>
        <div>
          <h3>Kontakt &amp; Person</h3>
          <p>Grundlegende Angaben zur ausgewählten Person.</p>
        </div>
        <v-btn v-if="isAdminForPW" text small class="info-header-action text-none" @click="resetPW(user)">
          <v-icon left small>mdi-lock-reset</v-icon>
          Passwort-Link
        </v-btn>
      </header>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field v-model="user.vorname" outlined dense dark hide-details label="Vorname" @change="saveEditing" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="user.nachname" outlined dense dark hide-details label="Nachname" @change="saveEditing" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="user.geburtstag" outlined dense dark hide-details label="Geburtstag" @change="saveEditing" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="user.telefon" outlined dense dark hide-details label="Telefon" type="tel" @change="saveEditing" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="user.email" outlined dense dark hide-details label="E-Mail" type="email" @change="saveEditing" />
        </v-col>
        <v-col v-if="isAdminForPW" cols="12" md="6">
          <v-text-field
            v-model="user.verifiedEmail"
            outlined
            dense
            dark
            hide-details
            label="Verifizierte E-Mail"
            type="email"
            @change="saveEditing" />
        </v-col>
        <v-col v-if="user.tendays_date && isAdmin" cols="12" md="6">
          <v-text-field v-model="user.tendays_date" outlined dense dark hide-details label="10-Tage-Startdatum" @change="saveEditing" />
        </v-col>
      </v-row>
    </section>

    <section class="info-panel">
      <header class="info-panel__header">
        <span class="info-panel__icon info-panel__icon--blue"><v-icon>mdi-map-marker-outline</v-icon></span>
        <div>
          <h3>Adresse</h3>
          <p>Postanschrift und Wohnort.</p>
        </div>
      </header>
      <v-row dense>
        <v-col cols="12">
          <v-text-field v-model="user.adresse_1" outlined dense dark hide-details label="Adresse" @change="saveEditing" />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field v-model="user.plz" outlined dense dark hide-details label="PLZ" inputmode="numeric" @change="saveEditing" />
        </v-col>
        <v-col cols="12" sm="8">
          <v-text-field v-model="user.stadt" outlined dense dark hide-details label="Ort" @change="saveEditing" />
        </v-col>
      </v-row>
    </section>

    <section class="info-panel">
      <header class="info-panel__header">
        <span class="info-panel__icon info-panel__icon--amber"><v-icon>mdi-car-outline</v-icon></span>
        <div>
          <h3>Fahrzeug-Kennzeichen</h3>
          <p>Bis zu zehn Kennzeichen für die bestehende Kennzeichensuche.</p>
        </div>
        <v-btn text small color="primary" class="info-header-action text-none" :disabled="user.licensePlates.length >= 10" @click="addLicensePlate">
          <v-icon left small>mdi-plus</v-icon>
          Hinzufügen
        </v-btn>
      </header>
      <v-row dense>
        <v-col v-for="(plate, index) in user.licensePlates" :key="index" cols="12" md="6">
          <div class="plate-field">
            <v-text-field
              v-model="user.licensePlates[index]"
              outlined
              dense
              dark
              hide-details
              :label="`Kennzeichen ${index + 1}`"
              @change="saveEditing" />
            <v-btn
              v-if="user.licensePlates.length > 1"
              icon
              small
              color="error"
              title="Kennzeichen entfernen"
              aria-label="Kennzeichen entfernen"
              @click="removeLicensePlate(index)">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </section>

    <section v-show="userService.hasRight('g_manageRights')" class="info-panel info-panel--rights">
      <header class="info-panel__header">
        <span class="info-panel__icon info-panel__icon--violet"><v-icon>mdi-shield-account-outline</v-icon></span>
        <div>
          <h3>Rollen &amp; Rechte</h3>
        </div>
      </header>
      <v-row dense>
        <v-col cols="12" lg="6">
          <v-autocomplete
            v-model="selected.groups"
            :items="roles"
            item-value="_id"
            item-text="title"
            outlined
            dense
            dark
            hide-details
            chips
            clearable
            deletable-chips
            multiple
            small-chips
            label="Gruppenrechte"
            no-data-text="Keine Gruppenrechte verfügbar"
            @change="saveEditing" />
        </v-col>
        <v-col cols="12" lg="6">
          <v-autocomplete
            v-model="selected.specialRights"
            :items="specialRightsAutocomplete"
            item-value="_id"
            :item-text="getSpecialRightItemText"
            outlined
            dense
            dark
            hide-details
            chips
            clearable
            deletable-chips
            multiple
            small-chips
            label="Einzelrechte"
            no-data-text="Alle verfügbaren Rechte sind bereits abgedeckt"
            @change="saveEditing" />
        </v-col>
      </v-row>
    </section>

    <section class="info-panel info-panel--attendance">
      <header class="info-panel__header">
        <span class="info-panel__icon info-panel__icon--green"><v-icon>mdi-calendar-check-outline</v-icon></span>
        <div>
          <h3>Anwesenheit</h3>
          <p>Bestehende Anwesenheitstage aus dem Kundenkonto.</p>
        </div>
        <span class="attendance-count">{{ attendanceCount }} Tage erfasst</span>
      </header>
      <div class="attendance-layout">
        <div class="attendance-calendar">
          <v-date-picker dark locale="de-CH" color="primary" no-title full-width :value="$moment().format('YYYY-MM-DD')" :events="getEvents" />
        </div>

        <div class="attendance-history">
          <header class="attendance-history__header">
            <div>
              <strong>Letzte Studiobesuche</strong>
              <span>Check-in, Check-out und Aufenthaltsdauer</span>
            </div>
            <span v-if="recentAttendanceVisits.length" class="attendance-history__average"> Ø {{ averageVisitDuration }} </span>
          </header>

          <div v-if="loadingAttendance" class="attendance-history__state">
            <v-progress-circular :size="28" :width="3" color="primary" indeterminate />
            <span>Besuche werden geladen</span>
          </div>
          <div v-else-if="recentAttendanceVisits.length" class="attendance-visit-list">
            <article v-for="(visit, index) in recentAttendanceVisits" :key="`${visit.date}-${visit.start}-${index}`">
              <span class="attendance-visit__icon"><v-icon small>mdi-door-open</v-icon></span>
              <div class="attendance-visit__date">
                <strong>{{ $moment(visit.date).format("dd, DD.MM.YY") }}</strong>
                <span>{{ visit.start }} – {{ visit.end || "kein Check-out" }}</span>
              </div>
              <strong class="attendance-visit__duration">{{ visit.duration || "offen" }}</strong>
            </article>
          </div>
          <div v-else class="attendance-history__state">
            <v-icon>mdi-calendar-blank-outline</v-icon>
            <span>Noch keine Check-ins vorhanden</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"

export default {
  props: {
    user: {
      type: Object,
      required: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userService,
      roles: [],
      specialRights: [],
      selected: {
        groups: [],
        specialRights: [],
      },
      recentAttendanceEvents: [],
      loadingAttendance: false,
    }
  },
  async created() {
    this.loadRecentAttendance()

    if (!this.userService.hasRight("g_manageRights", "u_edit", "u_search")) {
      alert("Sie haben nicht genügend Rechte, um diesen Benutzer zu bearbeiten.")
      return
    }

    try {
      this.roles = await api.fetchAllRoles()
      this.specialRights = await api.fetchAllRights()
      const userRoles = await api.fetchRolesForUser(this.user._id)
      this.selected.specialRights = (userRoles.specialRights || []).map((right) => right._id)
      this.selected.groups = (userRoles.userRoles || []).map((role) => role._id)
    } catch (error) {
      this.$toast.error("Rollen und Rechte konnten nicht vollständig geladen werden")
    }
  },
  computed: {
    getEvents() {
      return this.user && this.user.attendance ? Object.keys(this.user.attendance) : []
    },
    attendanceCount() {
      return this.getEvents.length
    },
    attendanceVisits() {
      const groups = {}
      ;(this.recentAttendanceEvents || []).forEach((entry) => {
        if (!entry || !entry.type) return
        const date = this.normalizedAttendanceDate(entry)
        if (!date) return
        if (!groups[date]) groups[date] = []
        groups[date].push(entry)
      })

      const visits = []
      Object.keys(groups).forEach((date) => {
        const events = groups[date].slice().sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")))
        let activeVisit = null

        events.forEach((entry) => {
          if (entry.type === "checkin") {
            activeVisit = {
              date,
              start: this.formatAttendanceTime(entry.time),
              end: null,
              durationMinutes: null,
              duration: null,
              startedAt: this.attendanceMoment(date, entry.time),
            }
            visits.push(activeVisit)
          } else if (entry.type === "checkout" && activeVisit && !activeVisit.end) {
            const endedAt = this.attendanceMoment(date, entry.time)
            activeVisit.end = this.formatAttendanceTime(entry.time)
            if (activeVisit.startedAt && endedAt && endedAt.isSameOrAfter(activeVisit.startedAt)) {
              const minutes = endedAt.diff(activeVisit.startedAt, "minutes")
              if (minutes >= 0 && minutes <= 24 * 60) {
                activeVisit.durationMinutes = minutes
                activeVisit.duration = this.formatAttendanceDuration(minutes)
              }
            }
            activeVisit = null
          }
        })
      })

      return visits.sort((a, b) => {
        const timeA = a.startedAt ? a.startedAt.valueOf() : this.$moment(a.date).valueOf()
        const timeB = b.startedAt ? b.startedAt.valueOf() : this.$moment(b.date).valueOf()
        return timeB - timeA
      })
    },
    recentAttendanceVisits() {
      return this.attendanceVisits.slice(0, 10)
    },
    averageVisitDuration() {
      const completed = this.attendanceVisits.filter((visit) => Number.isFinite(visit.durationMinutes))
      if (!completed.length) return "–"
      const total = completed.reduce((sum, visit) => sum + visit.durationMinutes, 0)
      return this.formatAttendanceDuration(Math.round(total / completed.length))
    },
    isAdmin() {
      return localStorage.getItem("admin")
    },
    isAdminForPW() {
      return this.userService.getEmail() === "dominic.ammann.ch@gmail.com" || this.userService.getEmail() === "sebs@wellcomefit.ch"
    },
    specialRightsAutocomplete() {
      let filteredSpecialRights = []
      const ignoreRights = []
      const allActiveGroups = this.roles.filter((group) => this.selected.groups.includes(group._id))

      if (allActiveGroups.length) {
        allActiveGroups.forEach((group) => {
          ;(group.rights || []).forEach((right) => {
            const existingRight = filteredSpecialRights.find((entry) => entry._id === right._id)
            if (existingRight) {
              if (!existingRight.active && right.active) {
                filteredSpecialRights = filteredSpecialRights.filter((entry) => entry._id !== right._id)
              }
            } else if (!right.active && !ignoreRights.includes(right._id)) {
              filteredSpecialRights.push(right)
            }
            if (right.active) ignoreRights.push(right._id)
          })
        })
        return filteredSpecialRights
      }

      this.roles.forEach((group) => {
        ;(group.rights || []).forEach((right) => {
          if (!filteredSpecialRights.find((entry) => entry._id === right._id)) filteredSpecialRights.push(right)
        })
      })
      return filteredSpecialRights
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(value) {
        if (value && (!Array.isArray(value.licensePlates) || !value.licensePlates.length)) {
          this.$set(value, "licensePlates", [""])
        }
      },
    },
  },
  methods: {
    async loadRecentAttendance() {
      if (!this.user || !this.user._id) return
      this.loadingAttendance = true
      try {
        const events = await api.fetchRecentAttendance(this.user._id)
        this.recentAttendanceEvents = Array.isArray(events) ? events : []
      } catch (error) {
        this.recentAttendanceEvents = []
      } finally {
        this.loadingAttendance = false
      }
    },
    normalizedAttendanceDate(entry) {
      if (!entry || !entry.date) return null
      const parsed = this.$moment(entry.date, ["YYYY-MM-DD", this.$moment.ISO_8601], true)
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null
    },
    attendanceMoment(date, time) {
      if (!date || !time) return null
      const parsed = this.$moment(`${date} ${time}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true)
      return parsed.isValid() ? parsed : null
    },
    formatAttendanceTime(time) {
      if (!time) return "–"
      const parsed = this.$moment(time, ["HH:mm:ss", "HH:mm"], true)
      return parsed.isValid() ? parsed.format("HH:mm") : String(time).slice(0, 5)
    },
    formatAttendanceDuration(minutes) {
      const safeMinutes = Math.max(0, Number(minutes) || 0)
      const hours = Math.floor(safeMinutes / 60)
      const remainder = safeMinutes % 60
      if (!hours) return `${remainder} Min.`
      return remainder ? `${hours} Std. ${remainder} Min.` : `${hours} Std.`
    },
    async resetPW(user) {
      try {
        let status = await api.resetPWAdmin(user.email)
        let link = status.link
        if (window.location.href.includes("local")) {
          link = link.replace("https://app.wellcomefit.ch", "http://localhost:8080")
        }
        await navigator.clipboard.writeText(link)
        this.$toast.success("Passwort-Link wurde in die Zwischenablage kopiert")
      } catch (error) {
        this.$toast.error("Fehler beim Zurücksetzen des Passworts")
      }
    },
    getSpecialRightItemText(right) {
      return `${right.title} (${right.topic})`
    },
    saveEditing() {
      this.user.userRoles = this.selected.groups
      this.user.specialRights = this.selected.specialRights
      api
        .editRolle(this.user)
        .then(() => this.$toast.success("Benutzer erfolgreich bearbeitet"))
        .catch(() => this.$toast.error("Änderung konnte nicht gespeichert werden"))
    },
    addLicensePlate() {
      if (this.user.licensePlates.length < 10) this.user.licensePlates.push("")
    },
    removeLicensePlate(index) {
      if (this.user.licensePlates.length <= 1) return
      this.user.licensePlates.splice(index, 1)
      this.saveEditing()
    },
  },
}
</script>

<style scoped>
.customer-info-editor {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-panel {
  min-width: 0;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.025);
}

.info-panel--rights,
.info-panel--attendance {
  grid-column: 1 / -1;
}

.info-panel__header {
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 12px;
  margin-bottom: 17px;
}

.info-panel__header > div {
  min-width: 0;
}

.info-panel__header h3 {
  margin: 0;
  color: #ffffff;
  font-size: 16px;
}

.info-panel__header p {
  margin: 3px 0 0;
  color: #9fa5a1;
  font-size: 12px;
}

.info-panel__header code {
  color: #b5e8c8;
  font-size: 12px;
}

.info-panel__icon {
  display: inline-flex;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
}

.info-panel__icon--green {
  color: #77d49a;
  background: rgba(0, 150, 64, 0.13);
}

.info-panel__icon--blue {
  color: #78baf6;
  background: rgba(37, 135, 232, 0.13);
}

.info-panel__icon--amber {
  color: #f3c568;
  background: rgba(245, 158, 11, 0.12);
}

.info-panel__icon--violet {
  color: #d8b4fe;
  background: rgba(168, 85, 247, 0.12);
}

.info-panel__icon .v-icon {
  color: inherit !important;
}

.info-header-action .v-icon,
.plate-field .v-btn .v-icon {
  color: currentColor !important;
}

.info-header-action,
.attendance-count {
  flex: 0 0 auto;
  margin-left: auto;
}

.attendance-count {
  padding: 5px 9px;
  border: 1px solid rgba(0, 150, 64, 0.23);
  border-radius: 999px;
  color: #7bd49e;
  background: rgba(0, 150, 64, 0.09);
  font-size: 12px;
  font-weight: 750;
}

.plate-field {
  display: flex;
  align-items: center;
  gap: 6px;
}

.plate-field .v-input {
  flex: 1 1 auto;
}

.customer-info-editor ::v-deep .v-input__slot {
  min-height: 46px !important;
  border-radius: 11px !important;
  background: rgba(0, 0, 0, 0.13) !important;
}

.customer-info-editor ::v-deep .v-label,
.customer-info-editor ::v-deep input,
.customer-info-editor ::v-deep .v-select__selection,
.customer-info-editor ::v-deep .v-chip__content {
  font-size: 12px !important;
}

.attendance-layout {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(310px, 0.9fr) minmax(360px, 1.1fr);
}

.attendance-calendar,
.attendance-history {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 15px;
  background: rgba(0, 0, 0, 0.14);
}

.attendance-calendar ::v-deep .v-picker {
  color: #eef2ef !important;
  background: rgba(0, 0, 0, 0.14) !important;
}

.attendance-calendar ::v-deep .v-btn .v-icon,
.attendance-calendar ::v-deep .v-date-picker-header__value button,
.attendance-calendar ::v-deep .v-date-picker-table th,
.attendance-calendar ::v-deep .v-date-picker-table .v-btn {
  color: #d9dedb !important;
}

.attendance-calendar ::v-deep .v-date-picker-table .v-btn--active {
  color: #ffffff !important;
}

.attendance-calendar ::v-deep .v-date-picker-table {
  height: auto;
  padding-bottom: 12px;
}

.attendance-history {
  min-height: 342px;
  padding: 16px;
}

.attendance-history__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 2px 2px 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.attendance-history__header > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.attendance-history__header strong {
  color: #f3f6f4;
  font-size: 14px;
}

.attendance-history__header span {
  margin-top: 3px;
  color: #969e98;
  font-size: 12px;
}

.attendance-history__average {
  flex: 0 0 auto;
  margin: 0 !important;
  padding: 5px 9px;
  border: 1px solid rgba(0, 150, 64, 0.25);
  border-radius: 999px;
  color: #80d7a1 !important;
  background: rgba(0, 150, 64, 0.1);
  font-weight: 750;
}

.attendance-visit-list {
  display: grid;
  max-height: 360px;
  overflow-y: auto;
}

.attendance-visit-list article {
  display: grid;
  min-height: 56px;
  align-items: center;
  gap: 11px;
  padding: 9px 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
  grid-template-columns: 34px minmax(0, 1fr) auto;
}

.attendance-visit-list article:last-child {
  border-bottom: 0;
}

.attendance-visit__icon {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #71d093;
  background: rgba(0, 150, 64, 0.12);
}

.attendance-visit__icon .v-icon {
  color: inherit !important;
}

.attendance-visit__date {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.attendance-visit__date strong,
.attendance-visit__duration {
  color: #e9edea;
  font-size: 12px;
}

.attendance-visit__date span {
  margin-top: 2px;
  color: #969e98;
  font-size: 12px;
}

.attendance-visit__duration {
  color: #79d49b;
  white-space: nowrap;
}

.attendance-history__state {
  display: flex;
  min-height: 255px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #969e98;
  font-size: 12px;
  text-align: center;
}

.attendance-history__state .v-icon {
  color: #7f8882 !important;
}

@media (max-width: 900px) {
  .customer-info-editor {
    grid-template-columns: 1fr;
  }

  .info-panel--rights,
  .info-panel--attendance {
    grid-column: auto;
  }

  .attendance-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .info-panel {
    padding: 16px;
  }

  .info-panel__header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .info-header-action,
  .attendance-count {
    width: 100%;
    margin: 4px 0 0;
  }
}
</style>
